import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import type { ImageStyle } from 'react-native';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Title from 'src/presentation/components/atoms/Title';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import ImageViewer from 'src/presentation/components/organisms/ImageViewer';
import RemoteImage from 'src/presentation/components/molecules/RemoteImage';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import _ from 'lodash';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type { ApiPageGallery } from 'src/core/models/api/ApiPage';
import type { ApiPhoto } from 'src/core/models/api/ApiPhoto';

interface GalleryProps {
	data: ApiPageGallery;
}

export default function Gallery({ data }: GalleryProps) {
	const contentWidth = useContentWidth();
	const [isLoading, setIsLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [shown, setShown] = useState(false);
	const [curIndex, setCurIndex] = useState(0);
	const [emptyGalleryMessage, setEmptyGalleryMessage] = useState<string | null>(null);
	const [galleryData, setGalleryData] = useState<ApiPhoto[]>([]);
	const [, setId] = useState<number | null>(null);
	const [type, setType] = useState<number>(1);
	const [, setEventUuid] = useState<string>('');
	const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
	const [name, setName] = useState<string>('');

	// Square thumbnails: two per row for type 2, three for type 3, minus the margins around them.
	const sizedStyles = useMemo((): { typeTwo: ImageStyle; typeThree: ImageStyle } => {
		const twoUp = (contentWidth - 36) / 2;
		const threeUp = (contentWidth - 40) / 3;

		return {
			typeTwo: {
				width: twoUp,
				height: twoUp,
			},
			typeThree: {
				width: threeUp,
				height: threeUp,
			},
		};
	}, [contentWidth]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const galleryType = Number(data?.data?.cb_gallery_type ?? null) || 1;

		setId(data.id);
		setType(galleryType);
		setEventUuid(data.event_uuid);
		setThumbnail(data?.data?.page_image);
		setName(data.name);

		fetchGallery(data.id, data.event_uuid);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const closeViewer = () => {
		setShown(false);
		setCurIndex(0);
	};

	function openViewer(index: number) {
		setShown(true);
		setCurIndex(index);
	}

	function getImagesForGallery(imageType: string) {
		if (galleryData.length) {
			switch (type) {
				case 1: {
					const imageArray = _.map(galleryData, imageType);
					if (data?.data?.page_image) {
						imageArray.unshift(data.data.page_image);
					}
					return imageArray;
				}
				case 2:
				case 3:
					return _.map(galleryData, imageType);
			}
		}
	}

	function getStyleBasedOnType() {
		switch (type) {
			case 1:
				return styles.typeOne;
			case 2:
				return [styles.typeTwo, sizedStyles.typeTwo];
			case 3:
				return [styles.typeThree, sizedStyles.typeThree];
		}
	}

	function renderGalleryImages() {
		const style = getStyleBasedOnType();
		const imagesArray = getImagesForGallery('thumbnail');
		const images = imagesArray!.map((image, index) => {
			return (
				<TouchableOpacity key={index} onPress={() => openViewer(index)}>
					<RemoteImage resizeMode="cover" style={style} uri={image} />
				</TouchableOpacity>
			);
		});

		switch (type) {
			case 1:
				return (
					<View>
						<Title text={name} />
						{images}
					</View>
				);
			case 2:
				return (
					<View>
						<RemoteImage
							resizeMode="cover"
							style={{
								height: 210,
								width: '100%',
								marginBottom: Dimens.margin8,
							}}
							uri={thumbnail ?? ''}
						/>
						<Title text={name} />
						<View
							style={{
								marginLeft: 14,
								marginRight: 14,
								flexWrap: 'wrap',
								flexDirection: 'row',
								justifyContent: 'flex-start',
							}}
						>
							{images}
						</View>
					</View>
				);
			case 3:
				return (
					<View>
						<RemoteImage
							resizeMode="cover"
							style={{
								height: 210,
								width: '100%',
								marginBottom: Dimens.margin8,
							}}
							uri={thumbnail ?? ''}
						/>
						<Title text={name} />
						<View
							style={{
								marginLeft: 14,
								marginRight: 14,
								flexWrap: 'wrap',
								flexDirection: 'row',
								justifyContent: 'flex-start',
							}}
						>
							{images}
						</View>
					</View>
				);
		}
	}

	function fetchGallery(galleryId: number, galleryEventUuid: string) {
		useEventStore
			.getState()
			.getPage({ eventUuid: galleryEventUuid, pageId: galleryId })
			.then((pageData) => {
				let photos: ApiPhoto[] = [];
				if (pageData.type === 'GalleryHolder') {
					photos = pageData.children[0]?.photos ?? [];
				} else if (pageData.type === 'Gallery') {
					photos = pageData.photos ?? [];
				}

				if (photos.length) {
					setGalleryData(photos);
					setIsLoading(false);
				} else {
					setEmptyGalleryMessage(
						'Deze gallerij heeft nog geen afbeeldingen. Voeg een evenement toe om een galerij te bekijken.',
					);
					setIsLoading(false);
				}
			})
			.catch((error) => {
				console.error(error);
				// Stop the spinner and show a placeholder; the global toast already reports the failure.
				setIsLoading(false);
				setLoadFailed(true);
			});
	}

	if (isLoading) {
		return (
			<View style={styles.centered}>
				<Loader />
			</View>
		);
	}

	if (loadFailed) {
		return (
			<View style={styles.centered}>
				<EmptyState
					icon="cloud-offline-outline"
					message={Translations.errorFetchGallery}
					title={Translations.errorSomethingWentWrongShort}
				/>
			</View>
		);
	}

	if (emptyGalleryMessage) {
		return (
			<View style={{ flex: 1 }}>
				{thumbnail ? (
					<RemoteImage
						resizeMode="cover"
						style={{
							height: 210,
							width: '100%',
							marginBottom: Dimens.margin8,
						}}
						uri={thumbnail}
					/>
				) : null}
				<EmptyState icon="image-outline" message={Translations.textGalleryEmpty} title={Translations.textNoPhotosTitle} />
			</View>
		);
	}

	return (
		<ScrollView>
			{renderGalleryImages()}
			<ImageViewer
				eventName={name}
				imageUrls={getImagesForGallery('fullsize') ?? []}
				index={curIndex}
				shown={shown}
				onClose={closeViewer}
			/>
		</ScrollView>
	);
}

const styles = StyleSheet.create({
	typeOne: {
		height: 210,
		width: '100%',
		marginBottom: Dimens.margin10,
	},
	typeTwo: {
		margin: Dimens.margin2,
		alignSelf: 'flex-start',
		alignItems: 'center',
		justifyContent: 'center',
	},
	typeThree: {
		margin: Dimens.margin2,
		alignSelf: 'flex-start',
		alignItems: 'center',
		justifyContent: 'center',
	},
	centered: {
		flex: 1,
		justifyContent: 'center',
		alignItems: 'center',
	},
});
